/**
 * SystemAudioCapture - Captures system audio via Electron's desktopCapturer
 *
 * This class uses chromeMediaSource: "desktop" to capture system audio,
 * kills the video track to save resources, and connects the audio to the
 * global AudioContext for synchronized playback with video.
 */

type EventCallback = (...args: unknown[]) => void;

interface SystemAudioState {
  volume: number;

  muted: boolean;

  isCapturing: boolean;
}

class SystemAudioCapture {
  private audioContext: AudioContext;

  private destination: MediaStreamAudioDestinationNode;

  // Media nodes

  private stream: MediaStream | null = null;

  private sourceNode: MediaStreamAudioSourceNode | null = null;

  private gainNode: GainNode | null = null;

  private analyserNode: AnalyserNode | null = null;

  // State

  private _volume: number = 0;

  private _muted: boolean = true;

  private _isCapturing: boolean = false;

  // Event listeners

  private listeners: Map<string, EventCallback[]> = new Map();

  constructor(
    audioContext: AudioContext,

    destination: MediaStreamAudioDestinationNode,
  ) {
    this.audioContext = audioContext;

    this.destination = destination;
  }

  /**
   * Start capturing system audio.
   *
   * El path difiere por plataforma (ver startCapture): en Windows/Linux usa el
   * legacy `chromeMediaSource: "desktop"` vía getUserMedia; en macOS usa
   * `getDisplayMedia({ audio: true, video: false })` porque el path legacy
   * entrega silencio en mac.
   */

  async startCapture(): Promise<boolean> {
    if (this._isCapturing) {
      console.warn("🔊 SystemAudioCapture: Already capturing");

      return true;
    }

    try {
      // El path para capturar audio del sistema difiere por plataforma:

      //

      // - macOS: el `chromeMediaSource: "desktop"` de abajo entrega un track de

      //   audio que es SILENCIO (limitación de plataforma, sin error). El único

      //   camino que captura audio real es getDisplayMedia; el main devuelve el

      //   sink `audio:"loopback"` (ver screenCaptureHandlers) enrutado por

      //   CoreAudio taps (feature MacCatapSystemAudioLoopbackCapture en main.ts).

      //   Pedimos `video:false`: el handler responde audio-solo sin tocar la

      //   captura de pantalla (que exigiría otro permiso). Validado en macOS 26.

      //

      // - Windows/Linux: se mantiene el path legacy chromeMediaSource:desktop,

      //   que ahí sí captura audio y no dispara el selector de pantalla.

      // El path getDisplayMedia de mac SOLO sirve dentro de Electron: depende del
      // setDisplayMediaRequestHandler del main que responde `audio:"loopback"`.
      // En la PWA en un navegador macOS (sin electronAPI), getDisplayMedia
      // dispararía el selector de compartir-pantalla del SO al boot (initGlobalAudio
      // llama startCapture sin gesto). Por eso exigimos Electron; en mac-web cae al
      // path legacy, que sin chromeMediaSource real falla en silencio (sin prompt).
      const isElectron = typeof window !== "undefined" && !!window.electronAPI;

      const isMac =
        typeof navigator !== "undefined" && navigator.userAgent.includes("Mac");

      if (isMac && isElectron) {
        this.stream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,

          video: false,
        });
      } else {
        const constraints: MediaStreamConstraints = {
          audio: {
            // @ts-expect-error - Electron-specific constraint

            mandatory: {
              chromeMediaSource: "desktop",
            },
          },

          video: {
            // @ts-expect-error - Electron-specific constraint

            mandatory: {
              chromeMediaSource: "desktop",

              maxWidth: 1,

              maxHeight: 1,

              maxFrameRate: 1,
            },
          },
        };

        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      // Kill video tracks immediately - we only need audio

      this.stream.getVideoTracks().forEach((track) => {
        track.stop();
      });

      // Check if we have audio tracks

      const audioTracks = this.stream.getAudioTracks();

      if (audioTracks.length === 0) {
        console.warn("🔊 SystemAudioCapture: No audio tracks available");

        return false;
      }

      // Create audio nodes

      this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);

      this.gainNode = this.audioContext.createGain();

      // Apply initial volume/mute state

      this.applyGain();

      // Create analyser node for visualization

      this.analyserNode = this.audioContext.createAnalyser();

      this.analyserNode.fftSize = 512;

      this.analyserNode.smoothingTimeConstant = 0.8;

      // Connect: source -> gain -> analyser -> destination

      this.sourceNode.connect(this.gainNode);

      this.gainNode.connect(this.analyserNode);

      this.analyserNode.connect(this.destination);

      this._isCapturing = true;

      // Listen for track ended

      audioTracks[0].addEventListener("ended", () => {
        console.log("🔊 SystemAudioCapture: Audio track ended");

        this.stopCapture();
      });

      this.emitStateChanged();

      return true;
    } catch (error) {
      console.error("❌ SystemAudioCapture: Failed to start capture:", error);

      this._isCapturing = false;

      return false;
    }
  }

  /**
   * Stop capturing system audio
   */

  async stopCapture(): Promise<void> {
    if (!this._isCapturing) return;

    console.log("🔊 SystemAudioCapture: Stopping capture...");

    try {
      // Disconnect nodes

      if (this.sourceNode) {
        this.sourceNode.disconnect();

        this.sourceNode = null;
      }

      if (this.analyserNode) {
        this.analyserNode.disconnect();

        this.analyserNode = null;
      }

      if (this.gainNode) {
        this.gainNode.disconnect();

        this.gainNode = null;
      }

      // Stop all tracks

      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          track.stop();
        });

        this.stream = null;
      }

      this._isCapturing = false;

      this.emitStateChanged();
    } catch (error) {
      console.error("❌ SystemAudioCapture: Error stopping capture:", error);
    }
  }

  /**
   * Set volume (0-1)
   */

  setVolume(volume: number): void {
    this._volume = Math.max(0, Math.min(1, volume));

    this.applyGain();

    this.emitStateChanged();
  }

  /**
   * Set muted state
   */

  setMuted(muted: boolean): void {
    this._muted = muted;

    this.applyGain();

    this.emitStateChanged();
  }

  /**
   * Get current state
   */

  getState(): SystemAudioState {
    return {
      volume: this._volume,

      muted: this._muted,

      isCapturing: this._isCapturing,
    };
  }

  /**
   * Check if capturing
   */

  isCapturing(): boolean {
    return this._isCapturing;
  }

  /**
   * Get the analyser node for audio visualization
   */

  getAnalyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  // --- Private methods ---

  private applyGain(): void {
    if (!this.gainNode) return;

    const effectiveVolume = this._muted ? 0 : this._volume;

    this.gainNode.gain.setValueAtTime(
      effectiveVolume,

      this.audioContext.currentTime,
    );
  }

  private emitStateChanged(): void {
    this.emit("state-changed", this.getState());
  }

  // --- Event system ---

  on(event: string, callback: EventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }

    this.listeners.get(event)!.push(callback);
  }

  off(event: string, callback: EventCallback): void {
    const callbacks = this.listeners.get(event);

    if (callbacks) {
      const index = callbacks.indexOf(callback);

      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event: string, data?: unknown): void {
    const callbacks = this.listeners.get(event);

    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  // --- Cleanup ---

  destroy(): void {
    this.stopCapture();

    this.listeners.clear();

    console.log("🔊 SystemAudioCapture: Destroyed");
  }
}

export default SystemAudioCapture;
